import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { ENDPOINTS } from '../services/endpoints';
import { normalizeRank } from '../components/bids/constants/bidWizardConstants';

// Query defaults for employee rates
const employeeRatesQueryDefaults = {
  staleTime: 1000 * 60 * 5,     // 5 minutes
  gcTime: 1000 * 60 * 30,       // 30 minutes cache
  retry: 2,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

// ============================================================================
// GET ALL CURRENT RATES
// ============================================================================
export const useEmployeeRates = (includeInactive = false) => {
  const {
    data: employees,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['employeeRates', includeInactive],
    queryFn: async () => {
      const response = await apiService.get(
        ENDPOINTS.EMPLOYEE_RATES_LIST,
        { include_inactive: includeInactive }
      );
      return response;
    },
    ...employeeRatesQueryDefaults,
  });

  // Calculate stats
  const stats = useMemo(() => {
    if (!employees) return null;
    
    // Filter out deleted employees for stats
    const validEmployees = employees.filter(e => !e.employee?.includes('(deleted)'));
    
    const active = validEmployees.filter(e => e.active_status).length;
    const inactive = validEmployees.filter(e => !e.active_status).length;
    
    const avgRate = validEmployees.length > 0 
      ? validEmployees.reduce((sum, e) => sum + e.rate, 0) / validEmployees.length 
      : 0;
    
    const byType = validEmployees.reduce((acc, emp) => {
      const type = emp.type || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    
    return {
      total: validEmployees.length,
      active,
      inactive,
      avgRate,
      byType,
      minRate: Math.min(...validEmployees.map(e => e.rate)),
      maxRate: Math.max(...validEmployees.map(e => e.rate)),
    };
  }, [employees]);

  return {
    // Data
    employees: employees || [],
    stats,
    
    // Loading states
    loading: isLoading,
    refreshing: isFetching,
    
    // Error handling
    error: isError ? (error?.message || 'Failed to load employees') : null,
    isError,
    
    // Status
    initialized: !!employees,
    hasData: !!employees,
    
    // Actions
    refetch,
  };
};

// ============================================================================
// GET SPECIFIC EMPLOYEE
// ============================================================================
export const useEmployee = (qbEmployeeId) => {
  return useQuery({
    queryKey: ['employee', qbEmployeeId],
    queryFn: async () => {
      const response = await apiService.get(ENDPOINTS.EMPLOYEE_RATES_GET(qbEmployeeId));
      return response;
    },
    enabled: !!qbEmployeeId,
    ...employeeRatesQueryDefaults,
  });
};

// ============================================================================
// GET EMPLOYEE RATE HISTORY
// ============================================================================

export const useEmployeeHistory = (qbEmployeeId) => {
  return useQuery({
    queryKey: ['employeeHistory', qbEmployeeId],
    queryFn: async () => {
      const response = await apiService.get(ENDPOINTS.EMPLOYEE_RATES_HISTORY(qbEmployeeId));
      return response;
    },
    enabled: !!qbEmployeeId,
    staleTime: 1000 * 60 * 10, // 10 minutes (history changes less frequently)
  });
};

// ============================================================================
// GET RATES SUMMARY
// ============================================================================

export const useEmployeeRatesSummary = () => {
  return useQuery({
    queryKey: ['employeeRatesSummary'],
    queryFn: async () => {
      const response = await apiService.get(ENDPOINTS.EMPLOYEE_RATES_SUMMARY);
      return response.data || response;
    },
    ...employeeRatesQueryDefaults,
  });
};

// ============================================================================
// UPDATE EMPLOYEE RATE (MUTATION)
// ============================================================================

export const useUpdateEmployeeRate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ qbEmployeeId, data }) => {
      const response = await apiService.put(
        ENDPOINTS.EMPLOYEE_RATES_UPDATE(qbEmployeeId),
        data
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate ALL employee rates queries to force refetch
      queryClient.invalidateQueries({ 
        queryKey: ['employeeRates'],
        exact: false  // Invalidates all variations
      });
    },
    onError: (error) => {
      console.error('❌ Failed to update employee rate:', error);
    },
  });
};

// ============================================================================
// UPDATE EMPLOYEE STATUS (MUTATION)
// ============================================================================

export const useUpdateEmployeeStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ qbEmployeeId, active_status }) => {
      const response = await apiService.put(
        ENDPOINTS.EMPLOYEE_RATES_UPDATE(qbEmployeeId),
        { active_status }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch employee list
      queryClient.invalidateQueries({ queryKey: ['employeeRates'] });
      
      // Update specific employee cache
      queryClient.invalidateQueries({ queryKey: ['employee', variables.qbEmployeeId] });
      
      // Invalidate summary
      queryClient.invalidateQueries({ queryKey: ['employeeRatesSummary'] });
    },
    onError: (error) => {
      console.error('❌ Failed to update employee status:', error);
    },
  });
};

// ============================================================================
// SYNC EMPLOYEES FROM QUICKBOOKS (MUTATION)
// ============================================================================

export const useSyncEmployees = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const response = await apiService.post(ENDPOINTS.EMPLOYEE_RATES_SYNC, {});
      return response;
    },
    onSuccess: (data) => {
      // Invalidate employee list to refetch
      queryClient.invalidateQueries({ queryKey: ['employeeRates'] });
      queryClient.invalidateQueries({ queryKey: ['employeeRatesSummary'] });
      
      // Clear cache for employee-rates endpoints
      apiService.clearCache('employee-rates');
    },
    onError: (error) => {
      console.error('❌ Failed to sync employees:', error);
    },
  });
};

// ============================================================================
// CREATE EMPLOYEE (MUTATION) - Rarely used since we sync from QB
// ============================================================================

export const useCreateEmployeeRate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (employeeData) => {
      const response = await apiService.post(
        ENDPOINTS.EMPLOYEE_RATES_CREATE,
        employeeData
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeRates'] });
      queryClient.invalidateQueries({ queryKey: ['employeeRatesSummary'] });
    },
  });
};

// ============================================================================
// DEACTIVATE EMPLOYEE (MUTATION)
// ============================================================================

export const useDeactivateEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (qbEmployeeId) => {
      const response = await apiService.delete(
        ENDPOINTS.EMPLOYEE_RATES_DELETE(qbEmployeeId)
      );
      return response;
    },
    onSuccess: (data, qbEmployeeId) => {
      queryClient.invalidateQueries({ queryKey: ['employeeRates'] });
      queryClient.invalidateQueries({ queryKey: ['employee', qbEmployeeId] });
    },
  });
};

// ============================================================================
// BULK UPDATE EMPLOYEES (MUTATION)
// ============================================================================

export const useBulkUpdateRates = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updates) => {
      const response = await apiService.post(
        ENDPOINTS.EMPLOYEE_RATES_BULK_UPDATE,
        { updates }
      );
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['employeeRates'] });
      queryClient.invalidateQueries({ queryKey: ['employeeRatesSummary'] });
    },
  });
};


// ─── ADD THESE TO THE BOTTOM OF src/hooks/useEmployeeRates.js ────────────────
// Copy and paste from here to the end of the file.


/**
 * Build a { [canonicalRole]: medianRate } map from the employee rates array.
 * Mirrors the implementation in Berbay Pulse.
 */
export const buildRoleRateMap = (rates = []) => {
  if (!rates?.length) return {};

  const byRank = {};
  for (const r of rates) {
    const rawRank = (!r.rank || r.rank === 'None') ? 'Other' : r.rank;
    const rank    = normalizeRank(rawRank);
    if (!byRank[rank]) byRank[rank] = [];
    const rate = (r.rate || 0) <= 1 ? 50 : r.rate;
    byRank[rank].push(rate);
  }

  const roleRateMap = {};
  for (const [rank, rateList] of Object.entries(byRank)) {
    const sorted = [...rateList].sort((a, b) => a - b);
    const mid    = Math.floor(sorted.length / 2);
    let median   = sorted.length % 2 !== 0
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
    if (rank === 'Mktg Support' || rank === 'Other') median = Math.max(median, 50);
    roleRateMap[rank] = median;
  }

  return roleRateMap;
};

/**
 * Convenience hook used by Step4BuildServices.
 * Returns { rateMap, roles, isLoading, error }
 *   rateMap — { [canonicalRole]: medianRate }
 *   roles   — role names sorted high → low rate
 */
export const useRoleRateMap = ({ enabled = true } = {}) => {
  const { employees, loading: isLoading, error } = useEmployeeRates(false, enabled);
  const rateMap = buildRoleRateMap(employees);
  const roles   = Object.keys(rateMap).sort((a, b) => (rateMap[b] - rateMap[a]));
  return { rateMap, roles, isLoading, error };
};